import { autoScrollForElements } from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/element";
import {
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { Add, ArrowDownward, ArrowUpward, KeyboardArrowDown } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from "@mui/joy";
import { useParams } from "@tanstack/react-router";
import { ApiTask, TaskStatus, TaskType } from "griptonite-api-client/types/tasks";
import { useEffect, useRef, useState } from "react";

import { useCreateTask, useOptimisticTaskUpdateMutation } from "@/hooks/tasks";
import { useCurrentVenueQuery, useCurrentVenueUuid } from "@/hooks/venues";
import { convertBetweenSystems, convertToInternal } from "@/utils/grades";

import MiscTask from "../../assets/icons/MiscTask";
import StatusChip from "../TaskCard/StatusChip";
import TaskCard from "../TaskCard/TaskCard";
import {
  getGradeRowDropData,
  getStatusColumnDropData,
  isGradeRowDropData,
  isStatusColumnDropData,
  isTaskDragData,
} from "./dragData";
import TaskStatusSnackbar from "./TaskStatusSnackbar";
import { useTaskSetOverview } from "./useTaskSetOverview";
import { useTaskStatusChange } from "./useTaskStatusChange";

// TODO: move these to util
const labelsMap = {
  SET: "Setting",
  STRIP: "Stripping",
  MISC: "Misc",
};
const iconsMap = {
  SET: <ArrowUpward sx={{ color: "success.500" }} />,
  STRIP: <ArrowDownward sx={{ color: "danger.500" }} />,
  MISC: <MiscTask size="sm" sx={{ color: "#00C8D5" }} />,
};

// Housekeeping sets have no climb type, so only misc tasks can go in them.
const canAddTasks = (taskSet: { climbType: string | null } | undefined, type: TaskType) =>
  type === "MISC" || !!taskSet?.climbType;

export const TaskSetCardSection = ({
  type,
}: // isActive,
// setIsActive,
{
  type: TaskType;
  // isActive: boolean;
  // setIsActive: (isActive: boolean) => void;
}) => {
  useTaskSetOverview();
  const [isActive, setIsActive] = useState(true);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!scrollRef.current) return;
    return autoScrollForElements({
      element: scrollRef.current,
    });
  });

  return (
    <Accordion
      expanded={isActive}
      onChange={(_, expanded) => setIsActive(expanded)}
      sx={{
        backgroundColor: "neutral.100",
        borderBottom: "none",
      }}
    >
      <AccordionSummary
        indicator={<KeyboardArrowDown />}
        sx={{
          boxShadow: "lg",
          ".MuiAccordionSummary-button": {
            justifyContent: "flex-start",
            paddingY: 1,
            gap: 1,
            borderBottom: "1px solid",
            borderBottomColor: "neutral.200",
          },
          ".MuiAccordionSummary-indicator": {
            order: 0,
          },
          ".MuiAccordionSummary-content": {
            order: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            fontWeight: "bold",
            fontSize: "lg",
          },
        }}
      >
        <Box className="MuiAccordionSummary-content">
          {labelsMap[type]} {iconsMap[type]}
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          "&.Mui-expanded": {
            borderBottom: "1px solid",
            borderBottomColor: "neutral.200",
          },
          overflowX: "visible",
          ".MuiAccordionDetails-content": {
            padding: 0,
            overflowX: "visible",
          },
        }}
        slotProps={{
          content: {
            ref: scrollRef,
          },
        }}
      >
        <TaskSectionCards type={type} />
      </AccordionDetails>
    </Accordion>
  );
};

export const TaskSetCardSections = () => {
  const types = ["SET", "STRIP", "MISC"] as const;

  const venueUuid = useCurrentVenueUuid();

  const mutation = useOptimisticTaskUpdateMutation(venueUuid);
  const mutate = mutation.mutate;
  const changeStatus = useTaskStatusChange();

  useEffect(() => {
    return monitorForElements({
      onDrop({ source, location }) {
        const destination = location.current.dropTargets[0];
        if (!destination) {
          // if dropped outside of any drop targets
          return;
        }
        if (!isTaskDragData(source.data)) {
          return;
        }
        const { task } = source.data;
        const target = destination.data;

        if (isStatusColumnDropData(target)) {
          if (task.type !== target.type) {
            return;
          }
          // Confirms first where leaving Done changes what climbers see.
          changeStatus(task, target.status);
          return;
        }

        if (!isGradeRowDropData(target) || task.type !== target.type) {
          return;
        }
        if (
          !task.route ||
          convertBetweenSystems(
            task.route.gradeValue,
            task.route.gradeSystem,
            target.gradeSystem,
          ) === target.gradeValue
        ) {
          return;
        }
        if (task.type !== "SET") {
          return;
        }
        mutate({
          task,
          payload: {
            route: {
              gradeSystem: target.gradeSystem,
              gradeValue: target.gradeValue,
              gradeInternal: convertToInternal(target.gradeValue, target.gradeSystem),
            },
          },
        });
      },
    });
  }, [mutate, changeStatus]);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!scrollRef.current) return;
    return autoScrollForElements({
      element: scrollRef.current,
    });
  });

  return (
    <>
      <Box sx={{ overflow: "auto", flex: 1, minHeight: 0 }} ref={scrollRef}>
        <AccordionGroup sx={{}}>
          {types.map((type) => (
            <TaskSetCardSection
              key={type}
              type={type}
              // isActive={activeType === type}
              // setIsActive={() => {
              //   if (activeType !== type) {
              //     setActiveType(type);
              //   } else {
              //     setActiveType(null);
              //   }
              // }}
            />
          ))}
        </AccordionGroup>
      </Box>
      <TaskStatusSnackbar />
    </>
  );
};

export const TaskSectionCardsGradesRow = ({
  type,
  gradeSystem,
  gradeValue,
  tasks,
}: {
  type: TaskType;
  gradeSystem: string;
  gradeValue: string | null;
  tasks: ApiTask[];
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOver, setIsOver] = useState(false);
  const { selectedAssignee, taskSet } = useTaskSetOverview();
  const activeTaskUuid = useParams({ strict: false }).taskUuid;

  useEffect(() => {
    if (!ref.current) return;
    return dropTargetForElements({
      element: ref.current,
      onDragEnter: ({ source }) => {
        if (!isTaskDragData(source.data)) {
          return;
        }
        const { task } = source.data;
        // Only highlight rows the drop handler would actually move the task to.
        if (task.type !== type || task.type !== "SET" || !task.route) {
          return;
        }
        if (
          convertBetweenSystems(task.route.gradeValue, task.route.gradeSystem, gradeSystem) ===
          gradeValue
        ) {
          return;
        }
        setIsOver(true);
      },
      getData: () => getGradeRowDropData({ type, gradeSystem, gradeValue }),
      onDragLeave: () => setIsOver(false),
      onDrop: () => setIsOver(false),
    });
  }, [type, gradeSystem, gradeValue]);

  const { createTask } = useCreateTask();

  return (
    <Stack
      ref={ref}
      key={gradeValue}
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{
        padding: 1.5,
        minWidth: "min-content",
        borderBottom: "1px solid",
        borderBottomColor: "neutral.200",
        "&:last-of-type": {
          borderBottom: "none",
        },
        position: "relative",
        "@media (pointer: fine)": {
          ".add-button": {
            visibility: "hidden",
            opacity: 0,
            transition: "opacity 0.2s, visibility 0.2s",
          },
          "&:hover .add-button": {
            visibility: "visible",
            display: "flex",
            opacity: 1,
            flexShrink: 0,
          },
        },
        ...(isOver
          ? {
              backgroundColor: "primary.200",
              ".MuiCard-root, .MuiIconButton-root": {
                opacity: 0,
              },
            }
          : {}),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "72px",
          flexShrink: 0,
          height: "108px",
        }}
      >
        <Typography fontWeight="bold" level="body-lg">
          {gradeValue || "?"}
        </Typography>
      </Box>
      {tasks.map((task) => (
        <TaskCard
          key={task.uuid}
          task={task}
          displayMode="status"
          isActive={!!activeTaskUuid && task.uuid === activeTaskUuid}
        />
      ))}
      {canAddTasks(taskSet, type) ? (
        <IconButton
          className="add-button"
          size="sm"
          sx={{ height: "106px", minWidth: 0, width: "22px" }}
          color="primary"
          variant="solid"
          onClick={() =>
            taskSet &&
            createTask({
              type,
              venue: taskSet.venue,
              set: taskSet.uuid,
              status: "TODO",
              gradeSystem,
              gradeValue,
              assignees: selectedAssignee ? [selectedAssignee] : [],
              route:
                type === "SET" && taskSet.climbType
                  ? {
                      circuit: taskSet.circuit ? { uuid: taskSet.circuit.uuid } : null,
                      zone: taskSet.zone ? taskSet.zone.uuid : null,
                      map: null,
                      climbType: taskSet.climbType,
                      gradeSystem,
                      gradeValue,
                      venue: taskSet.venue,
                    }
                  : null,
            })
          }
        >
          <Add />
        </IconButton>
      ) : null}
    </Stack>
  );
};

export const TaskSectionCardsByGrade = ({ type }: { type: TaskType }) => {
  const { tasksByTypeGrade } = useTaskSetOverview();

  const tasksList = tasksByTypeGrade.get(type);
  return (
    <Stack>
      {tasksList?.map(({ gradeValue, tasks, gradeSystem }) => (
        <TaskSectionCardsGradesRow
          key={gradeValue}
          type={type}
          gradeSystem={gradeSystem}
          gradeValue={gradeValue}
          tasks={tasks}
        />
      ))}
    </Stack>
  );
};

const TaskSectionCardsStatusColumn = ({
  status,
  tasks,
  type,
}: {
  status: TaskStatus;
  tasks: ApiTask[];
  type: TaskType;
}) => {
  const [isOver, setIsOver] = useState(false);
  const { taskSet, selectedAssignee } = useTaskSetOverview();
  const { createTask } = useCreateTask();
  const { data: venue } = useCurrentVenueQuery();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    return dropTargetForElements({
      element: ref.current,
      onDragEnter: ({ source }) => {
        if (!isTaskDragData(source.data) || source.data.task.type !== type) {
          return;
        }
        setIsOver(true);
      },
      getData: () => getStatusColumnDropData({ type, status }),
      onDragLeave: () => setIsOver(false),
      onDrop: () => setIsOver(false),
    });
  }, [type, status]);
  const activeTaskUuid = useParams({ strict: false }).taskUuid;

  return (
    <Box
      ref={ref}
      key={status}
      sx={{
        borderRight: "1px solid",
        borderRightColor: "neutral.200",
        padding: 1,
        paddingX: 1.5,
        paddingBottom: 1.5,
        "@media (pointer: fine)": {
          ".add-button": {
            visibility: "hidden",
            opacity: 0,
            transition: "opacity 0.2s, visibility 0.2s",
          },
          "&:hover .add-button": {
            visibility: "visible",
            opacity: 1,
          },
        },
        ...(isOver
          ? {
              backgroundColor: "primary.200",
              ".MuiCard-root, .MuiButton-root": {
                opacity: 0,
              },
            }
          : {}),
      }}
    >
      <Box sx={{ marginBottom: 1.5 }}>
        <Typography textColor="neutral.600" level="body-sm">
          {status === "TODO" ? "To Do" : status === "IN_PROGRESS" ? "In Progress" : "Done"}
        </Typography>
      </Box>
      <Stack
        sx={{
          width: "288px",
          position: "relative",
        }}
        gap={1.5}
      >
        {isOver ? (
          <Box
            sx={{
              position: "absolute",
              top: "20px",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1,
            }}
          >
            <StatusChip status={status} />
          </Box>
        ) : null}
        {tasks.map((task) => (
          <TaskCard
            key={task.uuid}
            task={task}
            displayMode="grade"
            isActive={!!activeTaskUuid && task.uuid === activeTaskUuid}
          />
        ))}
        {status === "TODO" && canAddTasks(taskSet, type) && (
          <Button
            className="add-button"
            size="sm"
            onClick={() =>
              taskSet &&
              createTask({
                type,
                venue: taskSet.venue,
                set: taskSet.uuid,
                status,
                assignees: selectedAssignee ? [selectedAssignee] : [],
                route:
                  type === "SET" && taskSet.climbType
                    ? {
                        circuit: taskSet.circuit ? { uuid: taskSet.circuit.uuid } : null,
                        zone: taskSet.zone ? taskSet.zone.uuid : null,
                        map: null,
                        climbType: taskSet.climbType,
                        venue: taskSet.venue,
                        gradeSystem:
                          (taskSet.climbType === "BOULDER"
                            ? venue?.boulderGradeSystem
                            : venue?.sportGradeSystem) ?? null,
                        gradeValue: null,
                      }
                    : null,
              })
            }
          >
            <Add />
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export const TaskSectionCardsByStatus = ({ type }: { type: TaskType }) => {
  const statuses = ["TODO", "IN_PROGRESS", "DONE"] as const;
  const { tasksByTypeStatus } = useTaskSetOverview();
  const tasksMap = tasksByTypeStatus.get(type);
  return (
    <Stack direction="row">
      {statuses.map((status) => (
        <TaskSectionCardsStatusColumn
          key={status}
          status={status}
          tasks={tasksMap?.get(status) || []}
          type={type}
        />
      ))}
    </Stack>
  );
};

export const TaskSectionCards = ({ type }: { type: TaskType }) => {
  const { viewBy } = useTaskSetOverview();

  if (viewBy === "status") {
    return <TaskSectionCardsByStatus type={type} />;
  } else {
    return <TaskSectionCardsByGrade type={type} />;
  }
};
